import React from 'react';
import { useNavigate } from 'react-router-dom';
import { isEqual } from 'lodash-es';

import { useAppDispatch, useAppSelector } from '@/store';
import { authenticationSsoTelegram, authenticationSsoTelegramComplete } from '@/store/slices/authentication';
import { resolvePostAuthRoute } from '@/utils/post-auth-route';
import { resolveLoginRedirect } from '@/utils/login-redirect';
import { resolveConfiguratorFlowBase } from '@/utils/configurator-flow';
import { isEmailValid } from '@/utils/strings';
import { getErrorCode } from '@/utils/errors';
import { Button, Text } from '@/nova/components';
import { useLanguageSwitcher } from '@/nova/hooks/useLanguageSwitcher';

import { AuthLogo } from '../../login/components/AuthLogo';
import { LanguagePicker } from '../../login/components/LanguagePicker';
import { clearSsoAttempt, evaluateCallback, readSsoAttempt, writeLinkError, type SsoLinkError } from '../pkceState';
import { linkTelegram } from '../ssoClient';
import { ROUTE } from '../../../routes';

import { Card, ErrorText, Form, Input, LogoSlot, Message, Screen, Title } from './telegram-callback.styles';

type Phase = 'working' | 'needsEmail' | 'linkSent' | 'error';

/**
 * Classify a failed link-mode `linkTelegram` call into a one-shot code SsoConnections renders. Reuses
 * the app-wide `getErrorCode` (reads `data.error.code`) rather than a substring scan of the body, so
 * the literal 'SSO_ALREADY_LINKED' only counts when it IS the error code (the backend serializes that
 * OHError as a 500 with `{ error: { code } }`); a missing/expired auth token is a 401.
 */
const mapLinkError = (e: unknown): SsoLinkError => {
  if ((e as { response?: { status?: number } }).response?.status === 401) {
    return 'expired';
  }
  return getErrorCode(e) === 'SSO_ALREADY_LINKED' ? 'already' : 'failed';
};

/** Guard a stored `returnTo` to an internal path (defense-in-depth: reject `//host` / absolute URLs
 *  so a forged attempt can never turn the post-link redirect into an off-site navigation). */
const safeReturnTo = (path: string | undefined, fallback: string): string =>
  path && path.startsWith('/') && !path.startsWith('//') ? path : fallback;

/**
 * Telegram OIDC code-flow landing. Validates the anti-forgery `state` against the stored attempt
 * (fail-closed), posts the code + verifier to the backend, and finishes the sign-in. Three non-login
 * outcomes: an unknown Telegram identity asks for an email (then `/sso/telegram/complete`), an existing
 * account gets a "login link sent to email" notice, and a refusal/mismatch shows an error.
 *
 * Post-auth routing goes through an effect over LIVE selectors (not a snapshot taken inside the thunk's
 * `.then`): the awaited brands/session store updates may not have re-rendered this component yet when
 * the promise resolves, so a ref read there would route a returning owner as brand-less. This mirrors
 * the login page's routing gate.
 */
const TelegramCallback = (): React.ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const session = useAppSelector((state) => state.session, isEqual);
  const casinoInstance = useAppSelector((state) => state.casinoInstance, isEqual);
  // The owner's auth token, restored synchronously from localStorage on this post-redirect boot
  // (authentication.ts initialState). Present in LINK mode (the owner never logged out); used to call
  // linkTelegram. Stable from first render, so the one-shot mount effect below can read it directly.
  const accessToken = useAppSelector((state) => state.authentication.access_token);
  // Language switcher parity with /login (works unauthenticated); called unconditionally at the top.
  const { languages, currentLanguage, onChangeLanguage } = useLanguageSwitcher();

  const [phase, setPhase] = React.useState<Phase>('working');
  const [ticket, setTicket] = React.useState<string | null>(null);
  const [email, setEmail] = React.useState<string>('');
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  // Invalid email typed (synchronous validation) vs a rejected completion call (server/network).
  // Split so a failed complete shows a generic error, not "Please enter a valid email address."
  const [formError, setFormError] = React.useState<boolean>(false);
  const [serverError, setServerError] = React.useState<boolean>(false);
  const [resolved, setResolved] = React.useState<boolean>(false);
  // True while a LINK-mode (connect-from-settings) round-trip is calling linkTelegram, so the working
  // card reads "Connecting your account..." instead of the login "Signing you in...".
  const [linking, setLinking] = React.useState<boolean>(false);
  const redirectTargetRef = React.useRef<string | null>(null);
  // The onboarding session captured on the attempt before the redirect; held so the email-capture
  // completion (which clears the attempt) can still bind it on the newly created account.
  const boundSessionIdRef = React.useRef<string | undefined>(undefined);
  const ran = React.useRef<boolean>(false);

  React.useEffect(() => {
    if (ran.current) {
      return;
    }
    ran.current = true;

    const attempt = readSsoAttempt();
    const decision = evaluateCallback(window.location.search, attempt);
    // A LINK round-trip (connect-from-settings): the owner is already signed in and came from settings,
    // so EVERY non-success outcome below returns there with a one-shot error, never the login-centric
    // "return to login" dead-end (grill D1). This includes a cancel on Telegram's consent screen
    // (error=access_denied) and an anti-forgery mismatch, which the pre-exchange guards catch first.
    const isLink = attempt?.mode === 'link';
    const linkReturnTo = safeReturnTo(attempt?.returnTo, ROUTE.settingsSection('account'));

    if (decision.status === 'none') {
      navigate(isLink ? linkReturnTo : '/login', { replace: true });
      return;
    }
    if (decision.status !== 'ready' || !attempt || !decision.code) {
      // error (provider refused / cancelled) or mismatch (anti-forgery): never exchange.
      clearSsoAttempt();
      if (isLink) {
        writeLinkError('failed');
        navigate(linkReturnTo, { replace: true });
        return;
      }
      setPhase('error');
      return;
    }

    // LINK mode: link the identity to the current account (never the login flow, never a logout) and
    // return to settings. A failure returns a one-shot code SsoConnections shows there (grill D1/D2).
    if (attempt.mode === 'link') {
      clearSsoAttempt();
      if (!accessToken) {
        writeLinkError('expired');
        navigate(linkReturnTo, { replace: true });
        return;
      }
      setLinking(true);
      linkTelegram(accessToken, {
        code: decision.code,
        code_verifier: attempt.verifier,
        redirect_uri: attempt.redirectUri,
      })
        .then(() => navigate(linkReturnTo, { replace: true }))
        .catch((e) => {
          writeLinkError(mapLinkError(e));
          navigate(linkReturnTo, { replace: true });
        });
      return;
    }

    boundSessionIdRef.current = attempt.sessionId;
    dispatch(
      authenticationSsoTelegram({
        code: decision.code,
        code_verifier: attempt.verifier,
        redirect_uri: attempt.redirectUri,
        session_id: attempt.sessionId,
      }),
    )
      .unwrap()
      .then((result) => {
        if (result.success) {
          clearSsoAttempt();
          redirectTargetRef.current = resolveLoginRedirect(result.data.redirect_url);
          setResolved(true);
          return;
        }
        if ('needsEmail' in result) {
          // Keep the attempt cleared: the ticket now carries the identity for the completion call.
          clearSsoAttempt();
          setTicket(result.ssoTicket);
          setPhase('needsEmail');
          return;
        }
        clearSsoAttempt();
        setPhase('linkSent');
      })
      .catch(() => {
        clearSsoAttempt();
        setPhase('error');
      });
  }, [dispatch, navigate, accessToken]);

  // Route on once a sign-in has settled, reading LIVE session/brands (never a stale snapshot). Mirrors
  // the login page: a server `redirect_url` wins, else the brand-state default.
  React.useEffect(() => {
    if (!resolved) {
      return;
    }
    if (session.loaded && !session.loading && casinoInstance.loaded) {
      navigate(
        redirectTargetRef.current ??
          resolvePostAuthRoute({
            sessionStatus: session.status,
            brandsLoaded: casinoInstance.loaded,
            brandsCount: casinoInstance.brands.length,
            configuratorBase: resolveConfiguratorFlowBase(),
          }),
        { replace: true },
      );
    }
  }, [resolved, session, casinoInstance, navigate]);

  const handleSubmit = React.useCallback<React.FormEventHandler<HTMLFormElement>>(
    (event) => {
      event.preventDefault();
      if (!ticket || !isEmailValid(email) || submitting) {
        setFormError(!isEmailValid(email));
        setServerError(false);
        return;
      }
      setFormError(false);
      setServerError(false);
      setSubmitting(true);
      dispatch(
        authenticationSsoTelegramComplete({ sso_ticket: ticket, email, session_id: boundSessionIdRef.current }),
      )
        .unwrap()
        .then((result) => {
          if (result.success) {
            redirectTargetRef.current = resolveLoginRedirect(result.data.redirect_url);
            setResolved(true);
            return;
          }
          setPhase('linkSent');
        })
        .catch(() => {
          // A rejected completion (expired/invalid ticket, network) is a server failure, not a
          // bad email, so surface the generic error and re-enable the form.
          setSubmitting(false);
          setServerError(true);
        });
    },
    [dispatch, email, submitting, ticket],
  );

  // One card frame for every outcome; the body is chosen by the resolved/phase order below so the
  // logo, card chrome and language switcher stay identical across states (matches /login).
  const cardBody = ((): React.ReactElement => {
    if (resolved || phase === 'working') {
      return (
        <Message>
          {linking ? <Text text="Connecting your account..." /> : <Text text="Signing you in..." />}
        </Message>
      );
    }
    if (phase === 'needsEmail') {
      return (
        <>
          <Title>
            <Text text="Before you continue" />
          </Title>
          <Message>
            <Text text="We need your email to save your work to your account and sign you back in." />
          </Message>
          <Form onSubmit={handleSubmit} noValidate>
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={formError}
            />
            {formError && (
              <ErrorText>
                <Text text="Please enter a valid email address." />
              </ErrorText>
            )}
            {serverError && (
              <ErrorText role="alert">
                <Text text="Something went wrong. Please try again." />
              </ErrorText>
            )}
            <Button type="submit" label="Continue" disabled={submitting} fullWidth />
          </Form>
        </>
      );
    }
    if (phase === 'linkSent') {
      return (
        <>
          <Title>
            <Text text="Check your email" />
          </Title>
          <Message>
            <Text text="We sent a link to your email to finish signing in." />
          </Message>
        </>
      );
    }
    // phase === 'error'
    return (
      <>
        <Title>
          <Text text="Sign-in could not be completed" />
        </Title>
        <Message>
          <Text text="Please return to the login page and try again." />
        </Message>
      </>
    );
  })();

  return (
    <Screen>
      <LogoSlot>
        <AuthLogo />
      </LogoSlot>
      <Card>{cardBody}</Card>
      <LanguagePicker languages={languages} currentLanguage={currentLanguage} onChange={onChangeLanguage} />
    </Screen>
  );
};

export default TelegramCallback;
